import { useRef, useState, type CSSProperties, type ReactNode } from "react";

import { DropIndicator } from "@skilitics/designer-ui-components";
import type { Collision, DragStartEvent, UniqueIdentifier } from "@skilitics/drag-drop";
import { useDragDropMonitor, useSortable } from "@skilitics/drag-drop";

import {
  createCanvasCollisionDetector,
  DND_LAYER_ID,
  getInsertPosition,
  type DraggableData,
  type DroppableData,
} from "../dnd";

interface Props {
  ancestorIds: number[];
  children?: ReactNode;
  hasChildLayers?: boolean;
  id: number;
  index: number;
  title?: string;
  typeId: string;
  setIsDropInsideEnabled?: (enabled: boolean) => void;
  onDraggingChange?: (isDragging: boolean) => void;
}

export const LayerSortable = ({
  ancestorIds,
  children,
  title,
  id,
  index,
  typeId,
  hasChildLayers,
  setIsDropInsideEnabled,
  onDraggingChange,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const activeSourceIdRef = useRef<UniqueIdentifier | undefined>(undefined);

  const { isDragging } = useSortable({
    element: ref,
    id: `${id}:${DND_LAYER_ID}`,
    group: `${ancestorIds.at(-1) ?? 0}`,
    index,
    collisionDetector: createCanvasCollisionDetector({
      inLayersPanel: true,
      orientation: "vertical",
      hasChildren: hasChildLayers,
      typeId,
      ancestorIds,
    }),
    data: {
      title,
      node: { type: "instance", typeId, ancestorIds, id },
      inLayersPanel: true,
      hasChildren: hasChildLayers,
      orientation: "vertical",
    } satisfies DroppableData & DraggableData,
  });

  const [insertPosition, setInsertPosition] = useState<ReturnType<typeof getInsertPosition>>();

  useDragDropMonitor({
    onDragStart: (event: DragStartEvent) => {
      activeSourceIdRef.current = event.operation.source?.id;
      if (event.operation.source?.id === `${id}:${DND_LAYER_ID}`) {
        onDraggingChange?.(true);
      }
    },
    onCollision: (event: { collisions: Collision[] }) => {
      const isThisDropTarget = event.collisions[0]?.id === `${id}:${DND_LAYER_ID}`;
      const position = isThisDropTarget ? getInsertPosition(event.collisions, activeSourceIdRef.current) : undefined;
      setInsertPosition(position);
      setIsDropInsideEnabled?.(position === "inside");
    },
    onDragEnd: () => {
      setInsertPosition(undefined);
      activeSourceIdRef.current = undefined;
      setIsDropInsideEnabled?.(false);
      onDraggingChange?.(false);
    },
  });

  const depth = ancestorIds.length;

  const style: CSSProperties = {
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={ref} style={style}>
      {children}
      {depth !== 0 && (insertPosition === "insertTop" || insertPosition === "insertBottom") && (
        <DropIndicator
          insertPosition={insertPosition}
          domRef={ref}
          offset={{ left: -(20 + depth * 10), top: 0 }}
          inLayersPanel={true}
        />
      )}
    </div>
  );
};
